import { useEffect, useState } from 'react';

/**
 * Operations-panel presence bridge (#197 / control-panel spec §0, §2).
 *
 * The two-queue guard: the Urgent Alert queue renders in the COP left rail on a
 * single monitor **or** on the operations panel (`/panel`) on dual monitors —
 * **never both**. To enforce that, each open `/panel` window announces its
 * presence on a same-origin BroadcastChannel (mirroring the camera pop-out
 * registry); the COP listens and, while any panel is open, moves the queue to the
 * panel and shows a pointer in its place.
 *
 * Presence is tracked as a Set of panel instance ids (so two panels, or a panel
 * that reloads, never leave a stale "open" latched): a panel announces `open` with
 * its id on mount and `close` on unload.
 */

export const PANEL_CHANNEL_NAME = 'tsr-panel';

export interface PanelMessage {
  type: 'open' | 'close';
  panelId: string;
}

function isPanelMessage(data: unknown): data is PanelMessage {
  if (typeof data !== 'object' || data === null) return false;
  const m = data as Record<string, unknown>;
  return (m.type === 'open' || m.type === 'close') && typeof m.panelId === 'string';
}

/** Pure reducer: fold a presence message into the open-panel-id set. */
export function applyPanelMessage(prev: ReadonlySet<string>, msg: PanelMessage): Set<string> {
  const next = new Set(prev);
  if (msg.type === 'open') next.add(msg.panelId);
  else next.delete(msg.panelId);
  return next;
}

/** Two-queue guard decision (§2): the COP renders the Urgent Alert queue only when
 *  no operations panel is open to host it. Pure so it's unit-testable. */
export function shouldRenderQueueInCop(panelOpen: boolean): boolean {
  return !panelOpen;
}

/**
 * COP-side hook: true while at least one `/panel` window is open. Drives the
 * two-queue guard (hide the COP queue) and the "Alerts on Operations Panel"
 * pointer. Inert (always false) without BroadcastChannel.
 */
export function usePanelPresence(): boolean {
  const [ids, setIds] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const channel = new BroadcastChannel(PANEL_CHANNEL_NAME);
    channel.onmessage = (e: MessageEvent) => {
      // Ignore the `__ping__` sentinel another COP may emit to wake panels — it
      // names no real panel, so it must never count as presence.
      if (isPanelMessage(e.data) && e.data.panelId !== '__ping__') {
        setIds((prev) => applyPanelMessage(prev, e.data));
      }
    };
    // A panel that opened *before* this COP mounted has already broadcast its
    // `open`; ask any live panels to re-announce so we don't miss them.
    channel.postMessage({ type: 'open', panelId: '__ping__' } satisfies PanelMessage);
    channel.postMessage({ type: 'close', panelId: '__ping__' } satisfies PanelMessage);
    return () => channel.close();
  }, []);
  return ids.size > 0;
}

/**
 * Panel-side effect: announce `open` for this panel instance on mount and `close`
 * on unmount / `beforeunload` (the latter covers the operator closing the window
 * via OS chrome, where React cleanup never runs). Also re-announces when another
 * window pings (a COP that mounted after this panel). Returns a cleanup for
 * useEffect.
 */
export function announcePanel(panelId: string): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => undefined;
  const channel = new BroadcastChannel(PANEL_CHANNEL_NAME);
  const announce = (): void =>
    channel.postMessage({ type: 'open', panelId } satisfies PanelMessage);
  announce();
  // Re-announce on a COP ping so a late-mounting COP learns we're open.
  channel.onmessage = (e: MessageEvent) => {
    if (isPanelMessage(e.data) && e.data.panelId === '__ping__' && e.data.type === 'open') {
      announce();
    }
  };
  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    channel.postMessage({ type: 'close', panelId } satisfies PanelMessage);
    channel.close();
  };
  window.addEventListener('beforeunload', close);
  return () => {
    window.removeEventListener('beforeunload', close);
    close();
  };
}
